import { Container, Grid, Header, KeyValuePairs, StatusIndicator } from '@amzn/awsui-components-react';
import React from 'react';
import { NotificationSummary } from 'src/models/dashboard/NotificationSummary';
import { TPELoadingSpinner } from '../shared/TPELoadingSpinner';

export default function NotificationSummaryView(props: {
  notificationSummaryResult: NotificationSummary,
  notificationSummaryLoading: boolean,
  /**
   * When true, render the stacked left-column layout used beside the MEC calendar card
   * (Summary + Completion stacked vertically, 2-column stats). When false (default), render the
   * original dashboard layout (Summary + Completion side by side, 4-column stats) so the widget
   * is unchanged for users without the accounting-calendar feature.
   */
  accountingCalendarLayout?: boolean,
}) {
  const { notificationSummaryResult, notificationSummaryLoading, accountingCalendarLayout = false } = props;

  const summaryContainer = (
    <Container
      data-class="summaryContainer"
      header={
        <Header description="Relevant to me">
          Summary
        </Header>
      }
    >
      <TPELoadingSpinner loading={notificationSummaryLoading}>
        <KeyValuePairs
          columns={accountingCalendarLayout ? 2 : 4}
          items={[
            { label: "Total items", value: <div className="displayLargeBold">{notificationSummaryResult?.totalItems}</div> },
            { label: "Open items", value: <div className="displayLargeBold">{notificationSummaryResult?.openItems}</div> },
            { label: "Overdue", value: <StatusIndicator data-class="dashboardSummaryStatusIndicator" type="warning" colorOverride="red"><span className="displayLargeBold">{notificationSummaryResult?.overdueItems}</span></StatusIndicator> },
            { label: "Due soon", value: <StatusIndicator data-class="dashboardSummaryStatusIndicator" type="warning"><span className="displayLargeBold">{notificationSummaryResult?.dueSoonItems}</span></StatusIndicator> }
          ]}
        />
      </TPELoadingSpinner>
    </Container>
  );

  const completionContainer = (
    <Container
      data-class="summaryContainer"
      header={
          <Header>Completion</Header>
      }
    >
      <TPELoadingSpinner loading={notificationSummaryLoading}>
        <div className="completionDiv">
          <span className="displayLargeBold">{notificationSummaryResult?.percentageCompletion}%</span>
          <div>of total items have been completed</div>
        </div>
      </TPELoadingSpinner>
    </Container>
  );

  // New calendar dashboard: stack Summary + Completion vertically to form the left column beside
  // the MEC calendar card. The column fills the Grid row height (Completion grows to fill the gap).
  if (accountingCalendarLayout) {
    return (
      <div className="dashboardSummaryColumn">
        {summaryContainer}
        {completionContainer}
      </div>
    );
  }

  // Original dashboard layout: Summary and Completion side by side.
  return (
    <Grid
      gridDefinition={[
        { colspan: { default: 3, xxs: 9 } },
        { colspan: { default: 9, xxs: 3 } }
      ]}
    >
      {summaryContainer}
      {completionContainer}
    </Grid>
  );
}
